import {isoDateUtcSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import {z} from 'zod';
import {
  AiCampaignMetric,
  AiDSP,
  AudiencesSource,
  CampaignObjective,
  DateAndTimeScheduling,
  LanguageSource,
  SportsElement,
  StrategySignal,
  UserLocationGranularity,
  UserLocationSource,
  WeatherElement,
  WebsitePastEngagementSignal,
} from './copilotCampaigns.enums';
import {AffectedAdObjects, AiNodeType, AiOutputType} from './copilotCampaigns.types';

const audienceSchema = z.object({
  id: z.number(),
  name: z.string().describe('The audience name.'),
});

// Before dispatching to the brief or strategy agent, the agent's preDispatch function will run with these parameters
function campaignWorkflowPreDispatchParamsSchema() {
  return z.object({
    campaignId: z.number(),
    versionNumber: z.string().nullable().optional().describe('The version of the campaign'),
  });
}

const briefSchema = z
  .object({
    objective: z.nativeEnum(CampaignObjective).describe('Campaign objective').nullable().optional(),
    startDate: isoDateUtcSchema
      .describe('Campaign start date. Display this in a human-readable format.')
      .nullable()
      .optional(),
    endDate: isoDateUtcSchema
      .describe('Campaign end date. Display this in a human-readable format.')
      .nullable()
      .optional(),
    campaignBudgetScope: z
      .number()
      .describe('Campaign Budget in USD')
      .positive()
      .nullable()
      .optional(),
    dspIds: z
      .array(z.nativeEnum(AiDSP))
      .describe('Media Partners (DSPs). Display these as a numbered list.')
      .nullable()
      .optional(),
    metricMeasure: z
      .array(z.nativeEnum(AiCampaignMetric))
      .describe('Campaign KPIs. Display these as a numbered list.')
      .nullable()
      .optional(),
    audiences: z.array(audienceSchema).describe('Campaign audiences').nullable().optional(),
    strategySummary: z
      .string()
      .nullable()
      .optional()
      .describe(
        'A summary of the strategy that was created. The user is not allowed to edit this field.',
      ),
    creativeInstructions: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Detailed instructions (markdown) for the creative designers in order to implement the strategy. The user is not allowed to edit this field.',
      ),
    feedInstructions: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Detailed instructions (markdown) for the feed creators in order to implement the strategy. The user is not allowed to edit this field.',
      ),
  })
  .describe('The campaign brief.');

// Define the object schema
const connectionSchema = z.object({
  fromNodeId: z.number(),
  fromOutputId: z.number(),
  toNodeId: z.number(),
});

// Define the array schema
const connectionsArraySchema = z.object({
  rootNodeId: z.number(),
  connections: z.array(connectionSchema),
});

const conceptNodeOutputSchema = z.object({
  nodeId: z.number().describe('Next Node Id'),
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(AiOutputType),
});

const conceptNodeOutputWithoutNodeSchema = conceptNodeOutputSchema.omit({nodeId: true});

const conceptNodeSchema = z.object({
  id: z.number().nullable().optional().describe('ID is mandatory if this node exists in the tree'),
  type: z.nativeEnum(AiNodeType),
  name: z.string(),
  depth: z.number().describe('depth in the decision tree'),
  description: z.string(),
  signalDescription: z.string().nullable().optional().describe('for Condition node only'),
  outputs: z
    .array(conceptNodeOutputSchema)
    .describe(
      'connect the node to next nodes in the decisioning tree. Empty for creative node which are leaf node',
    ),
  requireLocation: z
    .boolean()
    .nullable()
    .optional()
    .describe('for Creative node only. Whether or not to require location'),
  requireProduct: z
    .boolean()
    .nullable()
    .optional()
    .describe('for Creative node only. Whether or not to require product'),
});

const conceptNodeWithoutOutputNodesSchema = conceptNodeSchema.omit({outputs: true}).extend({
  outputs: z.array(conceptNodeOutputWithoutNodeSchema),
});

const addCardsSchema = z.object({
  nodes: z.array(conceptNodeWithoutOutputNodesSchema),
});

const strategyDecisionLogicSchema = z.object({
  signalName: z.nativeEnum(StrategySignal),
  signalProperties: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
    }),
  ),
  affectedAdObjects: z.nativeEnum(AffectedAdObjects),
  adElementsContainedInAdObject: z.array(z.string()),
  description: z.string(),
});

const conceptNodeSchemaWithoutDepth = conceptNodeSchema.omit({depth: true});

const deleteCardsSchema = z.object({
  nodes: z.array(conceptNodeSchemaWithoutDepth).describe('The nodes to delete'),
});

const audiencesPropertiesSchema = z.object({
  Audiences: z.array(audienceSchema),
  Source: z.nativeEnum(AudiencesSource),
});

const userLocationSchema = z.object({
  LocationGranularity: z.nativeEnum(UserLocationGranularity),
  Source: z.nativeEnum(UserLocationSource),
});

const languageSchema = z.object({
  Source: z.nativeEnum(LanguageSource),
});

const websitePastEngagementSchema = z.object({
  websiteSignal: z.nativeEnum(WebsitePastEngagementSignal),
});

const dateAndTimeSchema = z.object({
  type: z.nativeEnum(DateAndTimeScheduling),
});

const weatherSchema = z.object({
  weatherElement: z.nativeEnum(WeatherElement),
  isForecasted: z.boolean(),
});

const sportsDataSchema = z.object({
  dataElement: z.nativeEnum(SportsElement),
});

const productDataSchema = z.object({
  dataElement: z.string(),
});

const storeDataSchema = z.object({
  dataElement: z.string(),
});

const contentDataSchema = z.object({
  dataElement: z.string(),
});

const signalSchema = z.object({
  name: z.nativeEnum(StrategySignal),
  description: z.string(),
  propertiesSchema: z.any(),
});

const signalsSchema = z.array(signalSchema);

export {
  addCardsSchema,
  audienceSchema,
  audiencesPropertiesSchema,
  briefSchema,
  campaignWorkflowPreDispatchParamsSchema,
  conceptNodeOutputSchema,
  conceptNodeSchema,
  connectionsArraySchema,
  contentDataSchema,
  dateAndTimeSchema,
  deleteCardsSchema,
  languageSchema,
  productDataSchema,
  signalSchema,
  signalsSchema,
  sportsDataSchema,
  storeDataSchema,
  strategyDecisionLogicSchema,
  userLocationSchema,
  weatherSchema,
  websitePastEngagementSchema,
};
